import { useCallback, useMemo, useRef, useState } from 'react'

import useForkRef from './use-fork-ref'
type IState = string | number | boolean | undefined

function useToggle<T = boolean | undefined>(): {
  state: boolean
  toggle: (value?: T) => void
  setLeft: () => void
  setRight: () => void
  refState: React.RefObject<T>
}

function useToggle<T = IState>(
  defaultValue: T
): {
  state: T
  toggle: (value?: T) => void
  setLeft: () => void
  setRight: () => void
  refState: React.RefObject<T>
}

function useToggle<T = IState, U = IState>(
  defaultValue: T,
  reverseValue: U
): {
  state: T | U
  toggle: (value?: T | U) => void
  setLeft: () => void
  setRight: () => void
  refState: React.RefObject<T | U>
}

function useToggle<D extends IState = IState, R extends IState = IState>(
  defaultValue: D = false as D,
  reverseValue?: R,
  refState?: React.RefObject<D | R>
): {
  state: D | R
  toggle: (value?: D | R) => void
  setLeft: () => void
  setRight: () => void
  refState: React.RefObject<D | R>
} {
  const internalRef = useRef<D | R>()
  const _refState = useForkRef(internalRef, refState)
  const [state, setState] = useState<D | R>(defaultValue)
  const reverseValueOrigin = useMemo(
    () => (reverseValue === undefined ? !defaultValue : reverseValue) as D | R,
    [defaultValue, reverseValue]
  )

  // Toggle return value
  const toggle = useCallback(
    (value?: D | R) => {
      // Force return status value, suitable for click operation
      if (value !== undefined) {
        // eslint-disable-next-line no-console
        // console.log(`----useToggle toggle setState value: ${value}-----`)
        setState(value)
        typeof _refState === 'function' && _refState(value)
        return
      }

      setState(state => {
        const newState =
          state === defaultValue ? reverseValueOrigin : defaultValue
        // eslint-disable-next-line no-console
        // console.log(`----useToggle toggle setState newState: ${newState}-----`)
        typeof _refState === 'function' && _refState(newState)
        return newState
      })
    },
    [_refState, defaultValue, reverseValueOrigin]
  )

  // set default value
  const setLeft = useCallback(() => {
    // eslint-disable-next-line no-console
    // console.log(`----useToggle setLeft/on setState ${defaultValue}-----`)
    setState(defaultValue)
    typeof _refState === 'function' && _refState(defaultValue)
  }, [_refState, defaultValue])

  // Set the negation value
  const setRight = useCallback(() => {
    // eslint-disable-next-line no-console
    // console.log(
    //   `----useToggle setRight/off setState ${reverseValueOrigin}-----`
    // )
    setState(reverseValueOrigin)
    typeof _refState === 'function' && _refState(reverseValueOrigin)
  }, [_refState, reverseValueOrigin])

  return {
    state,
    toggle,
    setLeft,
    setRight,
    refState: (refState ? refState : internalRef) as React.RefObject<D | R>
  }
}

export default useToggle
